import { IRegisterWidgetData, IRegisterWidgetDataShort, TranslateService } from "@pageBuilder/typings";
import { RegisterScriptRenderer } from "./register-script-renderer";
import { IRenderingCommonContext } from "@commonRendering/types";
import { FormTruCaptchaScriptRenderer } from "../../form-widget/form-trucaptcha-script.renderer";
import { FormReCaptchaScriptRenderer } from "../../form-widget/form-recaptcha-script.renderer";
import { FormCaptchaRenderer } from "../../form-widget/form-captcha-renderer";
import { MembersAreaWidgetRendererBase } from "../common/members-area-widget-renderer-base";
import { GdprConstants } from "src/constants/gdpr-constants";
import { DomUtil } from "src/utils/dom-util";

export class RegisterWidgetRenderer extends MembersAreaWidgetRendererBase<IRegisterWidgetData>  {

    protected override readonly DATA_ATTRIBUTE = "data-register";

    private readonly scriptRenderer: RegisterScriptRenderer;
    private readonly recaptchaScriptRenderer: FormReCaptchaScriptRenderer;
    private readonly truCaptchaScriptRenderer: FormTruCaptchaScriptRenderer;

    private readonly captchaRenderer: FormCaptchaRenderer;
    private readonly translate: any;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);

        this.scriptRenderer = new RegisterScriptRenderer();
        this.recaptchaScriptRenderer = new FormReCaptchaScriptRenderer();
        this.truCaptchaScriptRenderer = new FormTruCaptchaScriptRenderer();

        this.captchaRenderer = new FormCaptchaRenderer();

        this.translate = TranslateService.translator(renderingCommonContext.data.lang);
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("registerWidget");
    }

    protected override renderInternal(element: HTMLElement, data: IRegisterWidgetData): HTMLElement {
        // notes: title and subtitle are rendered in Host.Static, because they change dependent on the returnUrl query parameter

        this.renderContent(element, data);

        this.setAttributes(element, data);

        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.scriptRenderer.renderLocal(element);

        this.renderStyles(element, data);
        this.renderCaptcha(element, data.disableReCaptcha);

        return element;
    }

    private renderContent(element: HTMLElement, data: IRegisterWidgetData): void {
        const content = element.querySelector<HTMLElement>(".widget-content");
        if (!content) {
            return;
        }

        this.renderRegisterForm(content, data);
        this.renderLoginText(content);
    }

    protected renderCaptcha(element: HTMLElement, disableReCaptcha: boolean | undefined): void {
        if (this.isEditorRendering || this.isPreviewRendering || disableReCaptcha) {
            return;
        }

        this.renderCaptchaScript(element);

        this.captchaRenderer.render(element, this.renderingCommonContext.data.captchaInfo);
    }

    private getUserAgreementInputId(data: IRegisterWidgetData): string {
        return `${data.id}-register-user-agreements`;
    }

    protected override prepareDataForSave(data: IRegisterWidgetData): IRegisterWidgetDataShort {
        return {
            showTermsOfService: data.showTermsOfService,
            showPrivacyPolicy: data.showPrivacyPolicy,
            isGdprEnabled: data.isGdprEnabled ?? false,
            isEmailVerificationEnabled: data.isEmailVerificationEnabled ?? false,
            fields: data.fields,
            submit: data.submit
        };
    }

    protected override getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`];
    }

    private renderRegisterForm(content: HTMLElement, data: IRegisterWidgetData): void {
        const form = this.ensureFormElement(content);

        DomUtil.deleteAllChildElements(form);

        const passwordFieldData = data.fields?.find(x => x.type === "Password");
        const emailFieldData = data.fields?.find(x => x.type === "Email");

        const labelsStyle = data.style.labels?.style ?? "standard";

        this.renderControl(form, emailFieldData, {
            inputName: "register-email",
            inputType: "email",
            inputMaxLength: this.EMAIL_MAX_LENGTH,
            autocomplete: this.isEditorRendering ? "off" : "email",
            labelText: this.translate.REGISTER.EMAIL,
            labelStyle: labelsStyle
        });

        this.renderControl(form, passwordFieldData, {
            inputName: "register-password",
            inputType: "password",
            inputMaxLength: this.PASSWORD_MAX_LENGTH,
            autocomplete: this.isEditorRendering ? "new-password" : "current-password",
            labelText: this.translate.REGISTER.PASSWORD,
            labelStyle: labelsStyle
        });

        if (this.isUserAgreementsCheckboxNecessary(data)) {
            this.renderUserAgreementsControl(form, data);
        }

        this.renderSubmitButton(form, data.style.submit?.theme ?? "primary", this.translate.REGISTER.SUBMIT_TEXT);
    }

    private isUserAgreementsCheckboxNecessary(data: IRegisterWidgetData): boolean {
        return (data.showTermsOfService || data.showPrivacyPolicy) && (data.isGdprEnabled ?? false);
    }

    private renderLoginText(container: HTMLElement): void {
        let loginTextElement = this.renderedDocument.querySelector<HTMLElement>(".login-text");
        if (loginTextElement) {
            return;
        }

        loginTextElement = document.createElement("span");
        loginTextElement.classList.add("login-text");
        loginTextElement.textContent = this.translate.REGISTER.LOG_IN_QUESTION;

        const loginLinkElement = document.createElement("a");
        loginLinkElement.classList.add("login-link");
        // href will be added at runtime
        loginLinkElement.textContent = this.translate.REGISTER.LOG_IN;

        const nonBreakingSpaceElement = document.createTextNode("\u00A0");

        loginTextElement.append(nonBreakingSpaceElement, loginLinkElement);

        container.appendChild(loginTextElement);
    }

    private renderUserAgreementsControl(container: HTMLElement, data: IRegisterWidgetData): void {
        const input = this.createFormInput();
        input.type = "checkbox";
        input.name = "user-agreements";
        input.id = this.getUserAgreementInputId(data);

        const label = document.createElement("label");
        label.classList.add("field-label");
        label.htmlFor = this.getUserAgreementInputId(data);
        this.renderUserAgreementsLabel(label);

        const checkbox = document.createElement("div");
        checkbox.classList.add("checkbox");
        checkbox.append(input, label);

        const description = document.createElement("span");
        description.classList.add("field-description", "gdpr-agreement");
        description.innerText = this.translate.GDPR.BY_CLICKING_THE + this.translate.REGISTER.TITLE + this.translate.GDPR.YOU_GIVE_US_PERMISSION;

        const field = document.createElement("div");
        field.classList.add("field");
        field.append(checkbox, description);

        container.appendChild(field);
    }

    private renderUserAgreementsLabel(labelElement: HTMLElement): void {
        const span = document.createElement("span");
        span.innerHTML = this.translate.GDPR.I_ACCEPT;
        span.innerHTML += this.createUserAgreementsLink();

        labelElement.appendChild(span);
    }

    private createUserAgreementsLink(): string {
        const a = document.createElement("a");
        a.classList.add("agreements-link");
        a.href = GdprConstants.PRIVACY_POLICY_PAGE_LINK;
        a.target = "_blank";
        a.innerText = this.translate.GDPR.PRIVACY_POLICY;

        return a.outerHTML;
    }

    protected renderCaptchaScript(element: HTMLElement): HTMLElement {
        this.cleanupScripts();

        if (this.renderingCommonContext.data.captchaInfo.captchaProtectionType === "TruCaptcha") {
            this.truCaptchaScriptRenderer.renderCommon(this.renderedDocument);
        } else if (this.renderingCommonContext.data.captchaInfo.captchaProtectionType === "ReCaptcha"){
            this.recaptchaScriptRenderer.renderCommon(this.renderedDocument);
        }

        return element;
    }

    private cleanupScripts(): void {
        this.truCaptchaScriptRenderer.cleanupCommon(this.renderedDocument);
        this.recaptchaScriptRenderer.cleanupCommon(this.renderedDocument);
    }

}
